/**
 * Content Manager - Users Service
 *
 * Service layer for user management operations using AWS API Gateway.
 */

import { apiClient, isAWSConfigured } from '../lib/awsConfig';
import type { User, UserRole, ApiResponse } from '../types';

// =============================================================================
// USERS SERVICE
// =============================================================================

export const usersService = {
  /**
   * Fetch all users
   */
  async fetchAll(): Promise<ApiResponse<User[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    try {
      const { data, error } = await apiClient.get<User[]>('/users');

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch active users only
   */
  async fetchActive(): Promise<ApiResponse<User[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    try {
      const { data, error } = await apiClient.get<User[]>('/users?status=active');

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching active users:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch a single user by ID
   */
  async fetchById(id: string): Promise<ApiResponse<User>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    try {
      const { data, error } = await apiClient.get<User>(`/users/${id}`);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch a user by email
   */
  async fetchByEmail(email: string): Promise<ApiResponse<User>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    try {
      const { data, error } = await apiClient.get<User>(`/users/by-email/${encodeURIComponent(email)}`);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user by email:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Create or update user profile (upsert)
   * Used after authentication to ensure user profile exists
   */
  async upsert(user: Partial<User> & { id: string; email: string }): Promise<ApiResponse<User>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    try {
      const { data, error } = await apiClient.put<User>('/users', {
        id: user.id,
        name: user.name || user.email.split('@')[0],
        email: user.email,
        role: user.role || 'Editor',
        department: user.department,
        status: user.status || 'active',
        avatarUrl: user.avatarUrl,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error upserting user:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update user profile
   */
  async update(id: string, updates: Partial<User>): Promise<ApiResponse<User>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    try {
      const { data, error } = await apiClient.patch<User>(`/users/${id}`, updates);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating user:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update user role
   */
  async updateRole(id: string, role: UserRole): Promise<ApiResponse<User>> {
    return this.update(id, { role });
  },

  /**
   * Deactivate a user
   */
  async deactivate(id: string): Promise<ApiResponse<User>> {
    return this.update(id, { status: 'inactive' });
  },

  /**
   * Activate a user
   */
  async activate(id: string): Promise<ApiResponse<User>> {
    return this.update(id, { status: 'active' });
  },

  /**
   * Delete a user (admin only)
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    try {
      const { error } = await apiClient.delete<void>(`/users/${id}`);

      if (error) throw error;

      return { data: undefined, error: null };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch users by department
   */
  async fetchByDepartment(department: string): Promise<ApiResponse<User[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    try {
      const { data, error } = await apiClient.get<User[]>(
        `/users?department=${encodeURIComponent(department)}&status=active`
      );

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching users by department:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get workload statistics for all users
   */
  async getWorkloadStats(): Promise<ApiResponse<Record<string, number>>> {
    if (!isAWSConfigured()) {
      return { data: {}, error: null };
    }

    try {
      const { data, error } = await apiClient.get<Record<string, number>>('/users/workload-stats');

      if (error) throw error;

      return { data: data || {}, error: null };
    } catch (error) {
      console.error('Error fetching workload stats:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Sync users from Azure Active Directory
   *
   * Automatically populates users from Azure AD using Microsoft Graph API.
   * This eliminates manual user creation - users are pulled directly from
   * your Azure AD tenant and matched by email for discussions, assignments, etc.
   *
   * @param accessToken - Azure AD access token with User.Read.All permission
   * @param options - Sync options
   */
  async syncFromAzureAD(
    accessToken: string,
    options?: {
      defaultRole?: UserRole;
      syncInactive?: boolean;
      departmentMapping?: Record<string, string>;
      /** Azure AD Group ID - only sync members of this group */
      groupId?: string;
      /** License SKU IDs - only sync users with these licenses (e.g., E3, E5) */
      licenseSkuIds?: string[];
    }
  ): Promise<ApiResponse<AzureADSyncResult>> {
    if (!isAWSConfigured()) {
      return { data: null, error: new Error('AWS not configured') };
    }

    try {
      const { data, error } = await apiClient.post<AzureADSyncResult>('/users/sync-azure', {
        accessToken,
        defaultRole: options?.defaultRole,
        syncInactive: options?.syncInactive,
        departmentMapping: options?.departmentMapping,
        groupId: options?.groupId,
        licenseSkuIds: options?.licenseSkuIds,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error syncing from Azure AD:', error);
      return { data: null, error: error as Error };
    }
  },
};

/**
 * Azure AD sync result type
 */
export interface AzureADSyncResult {
  message: string;
  summary: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
  };
  users: Array<{
    id: string;
    email: string;
    action: 'created' | 'updated' | 'skipped';
  }>;
}

export default usersService;
